// ─── WebSocket Message Types ───

export enum MessageType {
  // Connection
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  PING = 'ping',
  PONG = 'pong',

  // Lobby
  JOIN_GAME = 'join_game',
  LEAVE_GAME = 'leave_game',
  PICK_TEAM = 'pick_team',
  SET_NAME = 'set_name',
  LOADOUT_SELECT = 'loadout_select',
  LOADOUT_CONFIRM = 'loadout_confirm',

  // Player events
  PLAYER_JOINED = 'player_joined',
  PLAYER_LEFT = 'player_left',
  PLAYER_SPAWNED = 'player_spawned',
  PLAYER_DIED = 'player_died',
  PLAYER_RESPAWNED = 'player_respawned',
  MODE_SWITCH = 'mode_switch',

  // Game state
  GAME_STATE_SNAPSHOT = 'game_state_snapshot',
  WORLD_UPDATE = 'world_update',
  ROUND_START = 'round_start',
  ROUND_END = 'round_end',

  // Input
  PLAYER_INPUT = 'player_input',

  // Combat events
  PROJECTILE_SPAWN = 'projectile_spawn',
  PROJECTILE_UPDATE = 'projectile_update',
  PROJECTILE_DESTROY = 'projectile_destroy',
  EXPLOSION = 'explosion',
  DAMAGE = 'damage',
  KILL = 'kill',
  TERRAIN_DEFORM = 'terrain_deform',

  // Weapons
  WEAPON_FIRE = 'weapon_fire',
  WEAPON_RELOAD = 'weapon_reload',
  NAPALM_ZONE = 'napalm_zone',
  AMMO_UPDATE = 'ammo_update',

  // Chat
  CHAT = 'chat',
}

// ─── Message Payloads ───

import type {
  Vec3, Quat, TeamId, PlayerMode, Player, GameState,
  Projectile, CraterEvent, PlayerInput
} from './GameState.js';

export interface BaseMessage {
  type: MessageType;
  timestamp: number;
}

// Client → Server
export interface JoinGameMessage extends BaseMessage {
  type: MessageType.JOIN_GAME;
  playerName: string;
  preferredTeam?: TeamId;
}

export interface LeaveGameMessage extends BaseMessage {
  type: MessageType.LEAVE_GAME;
}

export interface LoadoutSelectMessage extends BaseMessage {
  type: MessageType.LOADOUT_SELECT;
  weaponIds: string[]; // 5 tank weapon IDs
}

export interface PlayerInputMessage extends BaseMessage {
  type: MessageType.PLAYER_INPUT;
  input: PlayerInput;
  sequence: number; // for reconciliation
}

// Server → Client
export interface GameStateSnapshotMessage extends BaseMessage {
  type: MessageType.GAME_STATE_SNAPSHOT;
  state: GameState;
  yourPlayerId: string;
}

export interface WorldUpdateMessage extends BaseMessage {
  type: MessageType.WORLD_UPDATE;
  players: PlayerSnapshot[];
  projectiles: Projectile[];
  roundTimeRemaining: number;
  serverTick: number;
}

export interface PlayerSnapshot {
  id: string;
  mode: PlayerMode;
  alive: boolean;
  tankHP: number;
  tankPosition: Vec3;
  tankRotation: Quat;
  turretYaw: number;
  barrelPitch: number;
  tankAlive: boolean;
  infantryHP: number;
  infantryPosition: Vec3;
  infantryRotation: Quat;
  spawnShieldTimer: number;
  activeWeaponIndex: number;
}

export interface PlayerJoinedMessage extends BaseMessage {
  type: MessageType.PLAYER_JOINED;
  player: Player;
}

export interface PlayerLeftMessage extends BaseMessage {
  type: MessageType.PLAYER_LEFT;
  playerId: string;
}

export interface ExplosionMessage extends BaseMessage {
  type: MessageType.EXPLOSION;
  position: Vec3;
  radius: number;
  weaponId: string;
}

export interface DamageMessage extends BaseMessage {
  type: MessageType.DAMAGE;
  targetId: string;
  amount: number;
  sourceId: string;
  weaponId: string;
  isHeadshot: boolean;
}

export interface KillMessage extends BaseMessage {
  type: MessageType.KILL;
  killerId: string;
  victimId: string;
  weaponId: string;
}

export interface TerrainDeformMessage extends BaseMessage {
  type: MessageType.TERRAIN_DEFORM;
  crater: CraterEvent;
}

export interface ProjectileSpawnMessage extends BaseMessage {
  type: MessageType.PROJECTILE_SPAWN;
  projectile: Projectile;
}

export interface RoundStartMessage extends BaseMessage {
  type: MessageType.ROUND_START;
  terrainSeed: number;
}

export interface RoundEndMessage extends BaseMessage {
  type: MessageType.ROUND_END;
  winnerTeam: TeamId | null;
  finalState: GameState;
}

export interface WeaponFireMessage extends BaseMessage {
  type: MessageType.WEAPON_FIRE;
  playerId: string;
  weaponId: string;
  origin: Vec3;
  direction: Vec3;
  powerScale?: number;
}

export interface NapalmZoneMessage extends BaseMessage {
  type: MessageType.NAPALM_ZONE;
  id: string;
  position: Vec3;
  radius: number;
  duration: number;
  ownerId: string;
}

export interface AmmoUpdateMessage extends BaseMessage {
  type: MessageType.AMMO_UPDATE;
  weaponAmmo: Record<string, number>; // weaponId -> remaining ammo (-1 = infinite)
}

export interface ModeSwitchMessage extends BaseMessage {
  type: MessageType.MODE_SWITCH;
  playerId: string;
  newMode: PlayerMode;
}

// Union type for all messages
export type GameMessage =
  | JoinGameMessage
  | LeaveGameMessage
  | LoadoutSelectMessage
  | PlayerInputMessage
  | GameStateSnapshotMessage
  | WorldUpdateMessage
  | PlayerJoinedMessage
  | PlayerLeftMessage
  | ExplosionMessage
  | DamageMessage
  | KillMessage
  | TerrainDeformMessage
  | ProjectileSpawnMessage
  | RoundStartMessage
  | RoundEndMessage
  | WeaponFireMessage
  | ModeSwitchMessage
  | NapalmZoneMessage
  | AmmoUpdateMessage;
